/**
 * Rig State store - Milestone 2 (2026-08-15, PLAN.md QUEUED "Rig States").
 * PWA-side library of banks + capture/recall. Same shape as
 * `timeline/store.ts`: localStorage-persisted library, a reactive signal,
 * CRUD helpers.
 *
 * Capture and recall are the actual "engine": capture samples
 * `liveState/resolve.ts`'s unified read (registry + send-cache) into a
 * chord; recall writes a chord back out through the same module's write
 * path (registry setters or `fireAction`), falling back to the bank's HOME
 * look for any union target the recalled look doesn't itself name.
 *
 * Authoring UI (`RigStateManager.tsx` - bank list -> bank editor -> look
 * editor) and the button-shaped item kind (`RigStateButtonItem`/
 * `RigStateButton.tsx`) shipped 2026-08-16, both consuming the mutators
 * below (`addTargetToLook`/`removeTargetFromLook`/`recaptureLookTarget`/
 * `setHomeLook`/`renameLook`). The UI adds targets via an explicit tap-to-
 * pick chip list, not drag-and-drop onto the bank - a deliberate deviation
 * from the Timeline live-state drawer's mechanism, made for a touch-first
 * product.
 *
 * Detach-on-recall (locked semantics #3) is built for LFOs (2026-10-18,
 * `detach.ts`), not yet for Macros (no OPBridge stop route). Not yet built:
 * OBS/DMX feedback states, blind NUDGE, Performance-Mode-gated re-capture,
 * timeline pre-roll wiring.
 */

import { createSignal, type Accessor } from 'solid-js';
import type { RigStateBank, RigStateLook, RigStateLibrary, RigStateChordEntry } from '@opbuttons/schema';
import { RIG_STATE_SCHEMA_VERSION } from '@opbuttons/schema';
import { ulid } from '../utils/ulid.ts';
import { readTarget, writeTarget } from '../liveState/resolve.ts';
import { lfosDriving, parseCcTarget } from './detach.ts';
import { activeProject, type LfoItem } from '../layout/projectStore.ts';
import { bridges } from '../opbridge/store.ts';
import { fanoutTargets, stopLfoAction } from '../opbridge/dispatch.ts';
import { lfoState, recordLfoState } from '../lfo/runtime.ts';

const RIG_STATE_KEY = 'opbuttons-rigstate-v1';

function emptyLibrary(): RigStateLibrary {
  return { schemaVersion: RIG_STATE_SCHEMA_VERSION, banks: [] };
}

function load(): RigStateLibrary {
  if (typeof localStorage === 'undefined') return emptyLibrary();
  try {
    const raw = localStorage.getItem(RIG_STATE_KEY);
    if (!raw) return emptyLibrary();
    const parsed = JSON.parse(raw) as RigStateLibrary;
    if (parsed?.schemaVersion !== RIG_STATE_SCHEMA_VERSION || !Array.isArray(parsed.banks)) return emptyLibrary();
    return parsed;
  } catch {
    return emptyLibrary();
  }
}

function persist(lib: RigStateLibrary): void {
  if (typeof localStorage === 'undefined') return;
  try { localStorage.setItem(RIG_STATE_KEY, JSON.stringify(lib)); } catch { /* private mode / quota */ }
}

const [library, setLibrary] = createSignal<RigStateLibrary>(load());
export const rigStateBanks = () => library().banks;

/** Whole-library accessor - read by `rigStateSync.ts` (OPBridge doc-store
 *  push/pull), same shape as `matrix/store.ts`'s `matrixLibrary`. */
export const rigStateLibrary: Accessor<RigStateLibrary> = library;

function setLibraryPersisted(next: RigStateLibrary): void {
  setLibrary(next);
  persist(next);
}

/** Replace the entire Rig State library - used by `rigStateSync.ts`'s pull
 *  path (a newer remote rev wins outright, same DEC-146-reversal shape as
 *  Matrix/Projects). Not for authoring mutations - those go through the
 *  bank/look helpers below so `modified` timestamps stay correct. */
export function setRigStateLibrary(next: RigStateLibrary): void {
  setLibraryPersisted(next);
}

export function getBank(bankId: string): RigStateBank | undefined {
  return library().banks.find((b) => b.id === bankId);
}

function upsertBank(bank: RigStateBank): void {
  const lib = library();
  const ix = lib.banks.findIndex((b) => b.id === bank.id);
  const withStamp: RigStateBank = { ...bank, modified: new Date().toISOString() };
  const banks = ix === -1 ? [...lib.banks, withStamp] : lib.banks.map((b, i) => (i === ix ? withStamp : b));
  setLibraryPersisted({ ...lib, banks });
}

export function createBank(name: string): RigStateBank {
  const now = new Date().toISOString();
  const bank: RigStateBank = { id: ulid(), name: name.trim() || 'Untitled Rig State', looks: [], created: now, modified: now };
  upsertBank(bank);
  return bank;
}

export function removeBank(bankId: string): void {
  const lib = library();
  setLibraryPersisted({ ...lib, banks: lib.banks.filter((b) => b.id !== bankId) });
}

/** Duplicate a Rig State with all its looks (2026-10-16: timelines, pages,
 *  projects and lanes could be duplicated, Rig States could not). The copy
 *  goes right after it as "<name> copy", every id new. */
export function duplicateBank(bankId: string): RigStateBank | null {
  const lib = library();
  const ix = lib.banks.findIndex((b) => b.id === bankId);
  const bank = lib.banks[ix];
  if (!bank) return null;
  const now = new Date().toISOString();
  const copy: RigStateBank = {
    ...structuredClone(bank),
    id: ulid(),
    name: `${bank.name} copy`,
    looks: bank.looks.map((l) => ({ ...structuredClone(l), id: ulid(), created: now, modified: now })),
    created: now,
    modified: now,
  };
  setLibraryPersisted({ ...lib, banks: [...lib.banks.slice(0, ix + 1), copy, ...lib.banks.slice(ix + 1)] });
  return copy;
}

/** Duplicate one look inside its Rig State, right after it, as "<name>
 *  copy". The copy is never HOME: a bank carries at most one. */
export function duplicateLook(bankId: string, lookId: string): RigStateLook | null {
  const bank = getBank(bankId);
  if (!bank) return null;
  const ix = bank.looks.findIndex((l) => l.id === lookId);
  const look = bank.looks[ix];
  if (!look) return null;
  const now = new Date().toISOString();
  const { isHome: _notHome, ...rest } = structuredClone(look);
  const copy: RigStateLook = { ...rest, id: ulid(), name: `${look.name} copy`, created: now, modified: now };
  upsertBank({ ...bank, looks: [...bank.looks.slice(0, ix + 1), copy, ...bank.looks.slice(ix + 1)] });
  return copy;
}

export function renameBank(bankId: string, name: string): void {
  const bank = getBank(bankId);
  if (!bank) return;
  upsertBank({ ...bank, name });
}

/** A bank's target set = the union of every look's captured targetIds
 *  (locked semantics #1 - never authored separately from the looks
 *  themselves). */
export function bankUnion(bank: RigStateBank): string[] {
  const seen = new Set<string>();
  for (const look of bank.looks) for (const entry of look.values) seen.add(entry.targetId);
  return [...seen];
}

export function homeLook(bank: RigStateBank): RigStateLook | undefined {
  return bank.looks.find((l) => l.isHome);
}

/** Capture a new look. `targetIds` defaults to the bank's current union -
 *  the normal case (resample everything the bank already tracks into a new
 *  named state). Pass an explicit list to bootstrap a bank's FIRST look
 *  (before any union exists) - the authoring UI itself only ever calls
 *  this with no `targetIds` (a fresh empty look), then widens it one
 *  target at a time via `addTargetToLook` below.
 *
 *  Targets with no known value are silently excluded, not invented -
 *  locked semantics #2 ("no hand-typed values... unreadable targets
 *  EXCLUDED from capture"). Returns null if the bank doesn't exist. */
export function captureLook(
  bankId: string,
  name: string,
  targetIds?: string[],
  opts?: { isHome?: boolean },
): RigStateLook | null {
  const bank = getBank(bankId);
  if (!bank) return null;
  const ids = targetIds ?? bankUnion(bank);
  const values: RigStateChordEntry[] = [];
  for (const targetId of ids) {
    const reading = readTarget(targetId);
    if (reading) values.push({ targetId, value: reading.value });
  }
  const now = new Date().toISOString();
  const look: RigStateLook = { id: ulid(), name: name.trim() || 'Untitled look', values, created: now, modified: now };
  if (opts?.isHome) look.isHome = true;
  // A bank should carry at most one HOME - demote any existing one so the
  // "exactly one HOME" invariant holds without a separate UI-side check.
  const looks = opts?.isHome
    ? bank.looks.map((l) => { const { isHome: _demoted, ...rest } = l; return rest; })
    : bank.looks;
  upsertBank({ ...bank, looks: [...looks, look] });
  return look;
}

export function removeLook(bankId: string, lookId: string): void {
  const bank = getBank(bankId);
  if (!bank) return;
  upsertBank({ ...bank, looks: bank.looks.filter((l) => l.id !== lookId) });
}

export function renameLook(bankId: string, lookId: string, name: string): void {
  const bank = getBank(bankId);
  if (!bank) return;
  const now = new Date().toISOString();
  upsertBank({
    ...bank,
    looks: bank.looks.map((l) => (l.id === lookId ? { ...l, name: name.trim() || l.name, modified: now } : l)),
  });
}

/** Explicit HOME setter for an EXISTING look (captureLook's `opts.isHome`
 *  only covers HOME-at-creation). Same "demote any existing HOME" invariant
 *  as captureLook - a bank carries at most one. */
export function setHomeLook(bankId: string, lookId: string): void {
  const bank = getBank(bankId);
  if (!bank) return;
  const now = new Date().toISOString();
  upsertBank({
    ...bank,
    looks: bank.looks.map((l) => {
      const isHome = l.id === lookId;
      const { isHome: _drop, ...rest } = l;
      return isHome ? { ...rest, isHome: true, modified: now } : rest;
    }),
  });
}

/**
 * The authoring primitive `captureLook`'s file header flagged as missing:
 * add ONE target to an EXISTING look without resampling everything else in
 * it. This is what widens a bank's union in the normal authoring flow (open
 * a look, add a target, it reads live right now) - `captureLook`'s
 * `targetIds` param stays as the bootstrap/resample path, this is the
 * incremental one.
 *
 * Same "no hand-typed values" rule as capture: an unreadable target is
 * silently excluded, never invented - returns false so the caller (the
 * authoring UI) can say so.  Re-adding a target already in this look
 * overwrites its value (same as recapture) rather than duplicating the
 * entry. */
export function addTargetToLook(bankId: string, lookId: string, targetId: string): boolean {
  const bank = getBank(bankId);
  if (!bank) return false;
  const look = bank.looks.find((l) => l.id === lookId);
  if (!look) return false;
  const reading = readTarget(targetId);
  if (!reading) return false;
  const now = new Date().toISOString();
  const withoutExisting = look.values.filter((v) => v.targetId !== targetId);
  const values: RigStateChordEntry[] = [...withoutExisting, { targetId, value: reading.value }];
  upsertBank({
    ...bank,
    looks: bank.looks.map((l) => (l.id === lookId ? { ...l, values, modified: now } : l)),
  });
  return true;
}

/** Re-read a target this look ALREADY carries and overwrite its value.
 *  Distinct from `addTargetToLook` only in that it never widens the look's
 *  target set - a no-op (returns false) if the look doesn't have this
 *  target yet, so it can't be used to sneak past the explicit-add step. */
export function recaptureLookTarget(bankId: string, lookId: string, targetId: string): boolean {
  const bank = getBank(bankId);
  const look = bank?.looks.find((l) => l.id === lookId);
  if (!look || !look.values.some((v) => v.targetId === targetId)) return false;
  return addTargetToLook(bankId, lookId, targetId);
}

/** Drop one target from THIS look only - per locked semantics #1, a look
 *  is free to leave a union target to HOME; this is how an author reverses
 *  an `addTargetToLook`. Never touches the bank's other looks or removes
 *  the target from the union while any other look still names it. */
export function removeTargetFromLook(bankId: string, lookId: string, targetId: string): void {
  const bank = getBank(bankId);
  if (!bank) return;
  const now = new Date().toISOString();
  upsertBank({
    ...bank,
    looks: bank.looks.map((l) =>
      l.id === lookId ? { ...l, values: l.values.filter((v) => v.targetId !== targetId), modified: now } : l),
  });
}

/** Manager modal open/close - colocated with the store, same convention as
 *  `timeline/store.ts`'s `timelineEditorTargetId`/`openTimelineEditor`
 *  rather than the shared `settings/store.ts` (Rig State is a rig-truth
 *  library like Timelines/Matrix, not a per-browser preference panel). */
const [managerOpenSig, setManagerOpenSig] = createSignal(false);
export const rigStateManagerOpen = managerOpenSig;
export function openRigStateManager(): void { setManagerOpenSig(true); }
export function closeRigStateManager(): void { setManagerOpenSig(false); }

export interface RecallOutcome {
  targetId: string;
  wrote: boolean;
  /** Which look actually supplied this target's value - the recalled look,
   *  or HOME when the recalled look left a union gap. Absent when neither
   *  had a value (a true gap - HOME itself doesn't cover this target). */
  source?: 'look' | 'home';
  detail?: string;
  /** LFOs that were driving this target and were stopped first (locked
   *  semantics #3, `rigState/detach.ts`). Absent when none were. */
  detached?: string[];
}

/** Stop every LFO driving one of these targets, before the look writes them.
 *  Every match is stopped whatever its last reported state: a stale poll must
 *  not leave a sweeping LFO to overwrite the look a beat later. The local
 *  mirror goes idle at once so the LFO's face stops glowing without waiting
 *  for the next poll. Returns the stopped LFOs by target id. */
async function detachLfos(targetIds: readonly string[]): Promise<Map<string, string[]>> {
  const lfos = (activeProject()?.pages ?? []).flatMap((pg) => pg.items)
    .filter((it): it is LfoItem => it.kind === 'lfo');
  const ports = bridges().map((b) => b.defaultMidiOutput).filter((p): p is string => !!p);
  const hit = lfosDriving(targetIds, lfos, ports) as LfoItem[];
  const byTarget = new Map<string, string[]>();
  if (hit.length === 0) return byTarget;
  await Promise.all(hit.flatMap((l) => fanoutTargets(l.bridgeIds).map((target) => stopLfoAction(l.id, target))));
  for (const l of hit) {
    recordLfoState({ op: 'lfo.state', lfoId: l.id, state: 'idle', currentValue: lfoState(l.id).currentValue, currentPhase: 0 } as Parameters<typeof recordLfoState>[0]);
    for (const t of targetIds) {
      const p = parseCcTarget(t);
      if (p && p.channel === l.channel + 1 && p.cc === l.ccNumber) byTarget.set(t, [...(byTarget.get(t) ?? []), l.name?.trim() || l.id]);
    }
  }
  return byTarget;
}

/** Recall a look: for every target in the bank's union, write the
 *  recalled look's value if it has one, else the bank's HOME value
 *  (locked semantics #1 - REPLACE-within-union; every look sets ALL union
 *  targets, one way or the other). A target neither the look nor HOME
 *  covers is a real gap - reported, not silently skipped, so a
 *  half-fired chord is visible to the caller (locked semantics: "surface
 *  a partial-recall warning ... when a chord half-fires").
 *
 *  Recalling HOME itself (bankId's home look) is just this function with
 *  HOME's own id - no special "blackout" path needed, since HOME already
 *  defines a value for every target that has one.
 *
 *  Modulation-aware (locked semantics #3, 2026-10-18): an LFO driving any
 *  union target is stopped BEFORE the writes (`detachLfos`). Macros are not
 *  (no stop route on OPBridge yet; `rigState/detach.ts`). */
export async function recallLook(bankId: string, lookId: string, bridgeTarget = ''): Promise<RecallOutcome[]> {
  const bank = getBank(bankId);
  if (!bank) return [];
  const look = bank.looks.find((l) => l.id === lookId);
  if (!look) return [];
  const home = homeLook(bank);
  const union = bankUnion(bank);
  const detached = await detachLfos(union);
  // Parallel across targets (2026-08-23 latency pass) - each `writeTarget`
  // call is an independent dispatch (its own MIDI CC/note POST, or a local
  // fire-and-forget setter for the 4 named targets); nothing about one
  // target's write depends on another's completing first, unlike the
  // push-then-arm engine primitives elsewhere in this codebase. A look
  // spanning N captured targets was previously paying N sequential
  // round-trips to recall as one "chord" - now one round-trip's worth of
  // wall-clock regardless of N. `Promise.all` preserves the mapped order,
  // so outcomes still line up with `union`.
  const outcomes = await Promise.all(union.map(async (targetId): Promise<RecallOutcome> => {
    const looked = look.values.find((v) => v.targetId === targetId);
    const homed = home?.values.find((v) => v.targetId === targetId);
    const entry = looked ?? homed;
    if (!entry) {
      return { targetId, wrote: false, detail: 'no value in this look or HOME' };
    }
    const result = await writeTarget(entry.targetId, entry.value, bridgeTarget);
    const source: 'look' | 'home' = looked ? 'look' : 'home';
    if (result.ok) {
      return { targetId, wrote: true, source };
    } else if (result.detail !== undefined) {
      return { targetId, wrote: false, source, detail: result.detail };
    } else {
      return { targetId, wrote: false, source };
    }
  }));
  return outcomes.map((o) => (detached.has(o.targetId) ? { ...o, detached: detached.get(o.targetId)! } : o));
}

/** Test-only escape hatch - smokes need a clean library between runs. */
export function _resetRigStateLibraryForTest(): void {
  setLibraryPersisted(emptyLibrary());
}
